/**
 * Hook: poll-aware `useCandidate` for the Resume tab (Phase 2.7.1).
 *
 * Wraps the standard `useCandidate` query with React Query's
 * `refetchInterval` predicate so the Resume tab re-fetches at ~2500ms
 * while `candidate.resume_formatting === true`. Stops polling
 * automatically the moment the flag flips back FALSE (success path),
 * `resume_formatting_failed_at IS NOT NULL` (failure path), or the
 * candidate has been removed from cache (component unmount triggers
 * React Query cleanup).
 *
 * The polling cadence (2500ms) mirrors the prototype canon at
 * `~/certain-prototype/public/pipeline.html` (`startResumeFormatPoll`).
 *
 * Uses the same cache key as the standard `useCandidate` hook so the
 * single-row cache is shared and the rest of the candidate-page
 * surfaces (Sidebar, Tabs, etc.) see the same data — they just don't
 * trigger the refetchInterval predicate.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Candidate } from "./useCandidates";

const POLL_INTERVAL_MS = 2500;

export function useCandidatePolling(
  candidateId: string | undefined,
): UseQueryResult<Candidate, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<Candidate, ApiError>({
    queryKey: ["candidate", tenantSlug, candidateId],
    queryFn: async () => {
      const r = await apiClient.get<Candidate>(
        `/api/t/${tenantSlug}/candidates/${candidateId}`,
      );
      return r.data;
    },
    enabled: !!tenantSlug && !!candidateId,
    staleTime: 30_000,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
    // Refetch every POLL_INTERVAL_MS while the worker is processing. The
    // predicate runs after each settled query — returning `false` halts
    // the interval. Polling resumes if a subsequent invalidate flips the
    // flag back to TRUE.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.resume_formatting === true) return POLL_INTERVAL_MS;
      return false;
    },
    // Keep polling while the tab is backgrounded (Architect §5 — the
    // prototype spec is "you can navigate away — this will finish in the
    // background"; the Resume tab should resolve on return regardless of
    // window focus). React Query default is `true`; we set explicitly
    // for forward-compat.
    refetchIntervalInBackground: true,
  });
}
